export type FortuneScoreKey = 'relationship' | 'work' | 'money' | 'condition';

export interface BirthDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly digits: string;
}

export type BirthValidation =
  | { readonly ok: true; readonly value: BirthDate }
  | { readonly ok: false; readonly error: string };

export interface FortuneScore {
  readonly key: FortuneScoreKey;
  readonly label: string;
  readonly emoji: string;
  readonly value: number;
  readonly note: string;
}

export interface FortuneResult {
  readonly dateKey: string;
  readonly dateLabel: string;
  readonly overall: number;
  readonly headline: string;
  readonly message: string;
  readonly scores: readonly FortuneScore[];
  readonly todo: string;
  readonly luckyColor: { readonly name: string; readonly hex: string };
  readonly luckyNumber: number;
  readonly direction: string;
}

const HEADLINES = [
  ['작은 용기가 행운을 불러요', '망설이던 일에 가볍게 첫발을 내디뎌 보세요. 완벽한 준비보다 오늘의 작은 실행이 더 멀리 데려다줄 거예요.'],
  ['천천히 볼수록 답이 보여요', '서두르지 않아도 괜찮아요. 평소 지나쳤던 단서 하나가 오늘의 흐름을 부드럽게 바꿔줄 수 있어요.'],
  ['반가운 소식이 가까이 있어요', '먼저 안부를 건네면 생각보다 따뜻한 답이 돌아올 거예요. 짧은 대화가 하루의 분위기를 환하게 만들어요.'],
  ['오늘은 내 감각을 믿어봐요', '여러 선택지 앞에서 첫 느낌이 꽤 좋은 나침반이 되어줄 날이에요. 마음이 편안해지는 쪽을 골라보세요.'],
  ['정리한 만큼 가벼워져요', '책상 한 칸이나 할 일 하나를 정리해 보세요. 작은 여백이 생기면 새로운 생각이 자연스럽게 들어와요.'],
  ['기다리던 흐름이 움직여요', '멈춘 것처럼 보였던 일이 조금씩 풀리기 시작해요. 조급하게 결론 내리지 말고 한 번 더 지켜보세요.'],
  ['솔직한 한마디가 통하는 날', '마음을 빙빙 돌려 말하기보다 부드럽고 분명하게 전해보세요. 진심은 생각보다 빠르게 닿을 거예요.'],
  ['익숙한 길에서 새로움을 찾아요', '평소 하던 일도 순서를 조금 바꾸면 신선한 답을 만날 수 있어요. 작은 변화가 좋은 자극이 됩니다.'],
  ['나를 챙기는 일이 먼저예요', '오늘의 에너지는 아껴 쓸수록 오래가요. 잠깐의 휴식과 따뜻한 식사가 가장 든든한 행운이 되어줘요.'],
  ['가벼운 시작이 좋은 결과로 이어져요', '부담을 크게 만들지 말고 10분만 해본다는 마음으로 시작해 보세요. 속도가 붙으면 생각보다 쉽게 끝낼 수 있어요.'],
  ['뜻밖의 조합에서 답이 나와요', '서로 어울리지 않아 보이던 생각을 연결해 보세요. 오늘은 엉뚱한 발상이 실용적인 해결책으로 바뀌는 날이에요.'],
  ['내 편이 생각보다 가까이 있어요', '혼자 해결하려 애쓰기보다 필요한 도움을 가볍게 요청해 보세요. 기꺼이 손을 내미는 사람이 나타날 거예요.'],
  ['말보다 행동이 빛나는 날이에요', '작은 약속 하나를 지키는 모습이 긴 설명보다 큰 신뢰를 만들어요. 할 수 있는 것부터 조용히 보여주세요.'],
  ['잠깐 멈추면 방향이 또렷해져요', '계속 달리는 것만이 정답은 아니에요. 숨을 고르고 우선순위를 하나만 정하면 마음도 일이도 단순해집니다.'],
  ['기분 좋은 우연을 만나게 돼요', '평소와 다른 길이나 메뉴를 선택해 보세요. 사소한 변주 속에서 오래 기억할 즐거움이 기다리고 있어요.'],
  ['오늘의 선택은 충분히 괜찮아요', '지나간 선택을 다시 채점하지 않아도 돼요. 지금 할 수 있는 다음 행동에 집중하면 흐름은 자연스럽게 좋아집니다.'],
  ['고마움을 표현할수록 풍성해져요', '마음속 감사 하나를 말이나 메시지로 전해보세요. 따뜻한 기운이 돌아와 하루를 단단하게 채워줄 거예요.'],
  ['집중력이 필요한 곳에 모여요', '할 일을 여러 개 펼치기보다 가장 중요한 하나를 먼저 끝내세요. 오늘은 몰입한 시간이 확실한 보람으로 남아요.'],
] as const;

const TODOS = [
  '미뤄둔 연락 한 통 보내기',
  '책상 위 한 칸 정리하기',
  '10분 동안 가볍게 걷기',
  '따뜻한 물 한 잔 천천히 마시기',
  '오늘 잘한 일 한 줄 적기',
  '평소보다 15분 일찍 움직이기',
  '좋아하는 노래 한 곡 듣기',
  '고마운 사람에게 짧게 안부 묻기',
  '가장 작은 할 일부터 끝내기',
  '창문을 열고 깊게 세 번 숨쉬기',
  '새로운 메뉴나 길 하나 골라보기',
  '잠들기 전 화면에서 20분 떨어지기',
] as const;

const COLORS = [
  { name: '라벤더 퍼플', hex: '#8b6ee8' },
  { name: '선샤인 옐로', hex: '#f6c94c' },
  { name: '세이지 그린', hex: '#7baa82' },
  { name: '코랄 오렌지', hex: '#f28c72' },
  { name: '스카이 블루', hex: '#65a9db' },
  { name: '로즈 핑크', hex: '#df7aa5' },
  { name: '크림 베이지', hex: '#d4ad75' },
  { name: '딥 네이비', hex: '#52628f' },
] as const;

const DIRECTIONS = ['동쪽', '서쪽', '남쪽', '북쪽', '남동쪽', '남서쪽', '북동쪽', '북서쪽'] as const;

const SCORE_META: readonly Omit<FortuneScore, 'value' | 'note'>[] = [
  { key: 'relationship', label: '관계운', emoji: '♡' },
  { key: 'work', label: '일·공부운', emoji: '✦' },
  { key: 'money', label: '금전운', emoji: '₩' },
  { key: 'condition', label: '컨디션', emoji: '☀' },
];

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function dateParts(dateKey: string): { year: number; month: number; day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) throw new Error('날짜 형식은 YYYY-MM-DD여야 합니다.');
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function isRealDate(year: number, month: number, day: number): boolean {
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return candidate.getUTCFullYear() === year
    && candidate.getUTCMonth() === month - 1
    && candidate.getUTCDate() === day;
}

function scoreNote(value: number): string {
  if (value >= 90) return '기분 좋은 흐름';
  if (value >= 80) return '한 걸음 더';
  if (value >= 70) return '차분히 좋아요';
  if (value >= 60) return '무리 없이 천천히';
  return '내 페이스 지키기';
}

export function getKstDateKey(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function formatBirthDigits(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}. ${digits.slice(4)}`;
  return `${digits.slice(0, 4)}. ${digits.slice(4, 6)}. ${digits.slice(6)}`;
}

export function validateBirthDate(value: string, todayKey: string = getKstDateKey()): BirthValidation {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return { ok: false, error: '생년월일 8자리를 모두 입력해 주세요.' };

  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));
  if (year < 1900) return { ok: false, error: '1900년 이후 생년월일을 입력해 주세요.' };
  if (!isRealDate(year, month, day)) return { ok: false, error: '실제로 존재하는 날짜인지 확인해 주세요.' };

  const today = dateParts(todayKey);
  const sortableBirth = year * 10_000 + month * 100 + day;
  const sortableToday = today.year * 10_000 + today.month * 100 + today.day;
  if (sortableBirth > sortableToday) return { ok: false, error: '미래의 날짜는 입력할 수 없어요.' };

  return { ok: true, value: { year, month, day, digits } };
}

export function generateFortune(birthValue: string, dateKey: string = getKstDateKey()): FortuneResult {
  const birth = validateBirthDate(birthValue, dateKey);
  if (!birth.ok) throw new Error(birth.error);

  const random = seededRandom(hashString(`todaytool-fortune-v1:${birth.value.digits}:${dateKey}`));
  const scores = SCORE_META.map((meta) => {
    const value = 45 + Math.floor(random() * 54);
    return { ...meta, value, note: scoreNote(value) };
  });
  const average = scores.reduce((sum, score) => sum + score.value, 0) / scores.length;
  const overall = clamp(Math.round(average + (random() - 0.5) * 14), 60, 95);
  const [headline, message] = HEADLINES[Math.floor(random() * HEADLINES.length)]!;
  const date = dateParts(dateKey);

  return {
    dateKey,
    dateLabel: `${date.month}월 ${date.day}일`,
    overall,
    headline,
    message,
    scores,
    todo: TODOS[Math.floor(random() * TODOS.length)]!,
    luckyColor: COLORS[Math.floor(random() * COLORS.length)]!,
    luckyNumber: 1 + Math.floor(random() * 9),
    direction: DIRECTIONS[Math.floor(random() * DIRECTIONS.length)]!,
  };
}
